/* global React */

// ─── Datos del modal (lang-aware) ──────────────────────────────────────────
// Cada campo de texto es un objeto { es, en } que se resuelve con `pick(...)`
// dentro de los componentes según el idioma activo en <html lang>.
const CONTRATAME_EMPRESA = {
  eyebrow: { es: "Para empresas y consultoras", en: "For companies and agencies" },
  title: { es: "Diseño, criterio y entregables concretos.", en: "Design, judgment and concrete deliverables." },
  sub: { es: "Trabajo con equipos que necesitan avanzar.", en: "I work with teams that need to move." },
  services: [
    {
      name: { es: "Validación de perfiles UX", en: "UX profile screening" },
      desc: {
        es: "Entrevistas técnicas para que contrates con criterio, no con suerte.",
        en: "Technical interviews so you hire with judgment, not luck.",
      },
      price: { es: "$90", en: "$90" },
      unit: { es: "por perfil", en: "per profile" },
      note: { es: "Más de 5 perfiles: armamos un paquete.", en: "More than 5 profiles: we build a package." },
    },
    {
      name: { es: "Consultoría por horas", en: "Consulting by the hour" },
      desc: {
        es: "Decisiones de diseño rápidas y bien fundamentadas cuando tu equipo las necesita.",
        en: "Fast, well-grounded design decisions when your team needs them.",
      },
      price: { es: "$120", en: "$120" },
      unit: { es: "por hora", en: "per hour" },
      note: {
        es: "$80 por hora comprometiendo 8 a 12 horas por mes.",
        en: "$80 per hour with an 8–12 hour monthly commitment.",
      },
    },
    {
      name: { es: "Part time / Full time", en: "Part-time / Full-time" },
      desc: {
        es: "Me integro a tu equipo. Beneficios a conversar.",
        en: "I join your team. Benefits to be discussed.",
      },
      price: { es: "Desde $35", en: "Starting at $35" },
      unit: { es: "por hora", en: "per hour" },
    },
    {
      name: { es: "Capacitación y desarrollo de equipos", en: "Training and team development" },
      desc: {
        es: "Workshops, programas de formación y acompañamiento para equipos de diseño. No hago el trabajo de campo por tu equipo — les enseño a hacerlo.",
        en: "Workshops, training programs and ongoing support for design teams. I don't do the field work for your team — I teach them to do it.",
      },
      custom: true,
      customBadge: { es: "A medida", en: "Tailored" },
    },
  ],
  cta: {
    label: { es: "Escribime", en: "Write me" },
    email: "anabelensevillaull@gmail.com",
  },
  pricesNote: {
    es: "*Los precios están expresados en USD.",
    en: "*Prices are in USD.",
  },
};

const CONTRATAME_DESIGNER = {
  eyebrow: { es: "Para diseñadores", en: "For designers" },
  title: { es: "Alguien con experiencia al lado tuyo.", en: "Someone with experience by your side." },
  pitchLead: {
    es: "Trabajo con diseñadores que saben lo que hacen pero están solos: en una empresa sin equipo, sin lead, sin nadie que les diga si van bien. Los que tienen síndrome del impostor. Los que quieren crecer pero no saben hacia dónde. Y también con los que están buscando trabajo y el portfolio no convierte, las entrevistas no cierran. ",
    en: "I work with designers who know what they're doing but are alone: at a company without a team, without a lead, without anyone to tell them they're on track. The ones with imposter syndrome. The ones who want to grow but don't know which direction. And with the ones job-hunting whose portfolio isn't converting and whose interviews don't close. ",
  },
  pitchStrong: {
    es: "Una sesión con las preguntas correctas cambia más que seis meses dando vueltas solo.",
    en: "One session with the right questions changes more than six months going in circles alone.",
  },
  modalities: [
    {
      name: { es: "Sesión 1:1", en: "1:1 Session" },
      desc: {
        es: "Portfolio, entrevistas, cambio de rol, problema específico de diseño. Una hora, sin vueltas.",
        en: "Portfolio, interviews, role change, a specific design problem. One hour, no fluff.",
      },
      pricePrefix: "USD",
      price: { es: "$100", en: "$100" },
      unit: { es: "por hora", en: "per hour" },
      cta: {
        label: { es: "Reservar sesión en Skool", en: "Book a session on Skool" },
        href: "https://www.skool.com/comunidaduxui/about?sc=1",
      },
      ctaNote: {
        es: "Para reservar necesitás ser miembro de la comunidad. Desde $9 por mes — accedés a recursos, desafíos, conexión con otros diseñadores y un espacio para sacar las dudas que no le preguntarías a nadie en el trabajo. Con 7 días de prueba gratis.",
        en: "You need to be a community member to book. Starting at $9/month — you get resources, challenges, connection with other designers, and a space to ask the questions you wouldn't ask anyone at work. 7-day free trial.",
      },
    },
    {
      name: { es: "Plan VIP", en: "VIP Plan" },
      badge: { es: "Cupos llenos", en: "Waitlist" },
      desc: {
        es: "Acompañamiento personalizado quincenal. Para el que quiere avanzar con alguien que ya recorrió el camino.",
        en: "Biweekly personalized support. For the person who wants to move forward with someone who's already walked the path.",
      },
    },
  ],
};

// ─── Helpers ───────────────────────────────────────────────────────────────
// Detecta el idioma activo del documento. Es el mismo flag que App.jsx
// sincroniza en <html lang> al cambiar el toggle ES/EN. Reactivo a cambios
// gracias a un MutationObserver montado a nivel componente.
function useDocumentLang() {
  const get = () => {
    if (typeof document === "undefined") return "es";
    const v = (document.documentElement.getAttribute("lang") || "es").toLowerCase();
    return v.startsWith("en") ? "en" : "es";
  };
  const [lang, setLang] = React.useState(get);
  React.useEffect(() => {
    const update = () => setLang(get());
    update();
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
    return () => mo.disconnect();
  }, []);
  return lang;
}

function pick(field, lang) {
  if (field && typeof field === "object" && (field.es || field.en)) {
    return field[lang] || field.es || field.en;
  }
  return field;
}

const TXT = {
  es: {
    close: "Cerrar",
    back: "Volver",
    bifTitle: "¿Con quién hablo?",
    bifSub: "Depende de tu respuesta, te cuento cómo puedo ayudarte.",
    bifOpt1Name: "Consultora o empresa",
    bifOpt1Desc: "Necesito una UX Lead para un proyecto, retainer o posición.",
    bifOpt2Name: "Soy diseñador/a",
    bifOpt2Desc: "Busco mentoría, revisión de portfolio o acompañamiento de carrera.",
  },
  en: {
    close: "Close",
    back: "Back",
    bifTitle: "Who am I talking to?",
    bifSub: "Depending on your answer, I'll tell you how I can help.",
    bifOpt1Name: "Agency or company",
    bifOpt1Desc: "I need a UX Lead for a project, retainer or role.",
    bifOpt2Name: "I'm a designer",
    bifOpt2Desc: "I'm looking for mentoring, portfolio review or career support.",
  },
};

// ─── Helpers de focus / scroll-lock ───────────────────────────────────────
function useBodyScrollLock(active) {
  React.useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

// ─── Subcomponentes por screen ────────────────────────────────────────────
// La "back" y la "close" viven en un topbar sticky a nivel del modal raíz
// (ver ContratameModal) — los screens ya no rendean su propio Volver.
function ContratameBifurcation({ onPick, lang }) {
  const tx = TXT[lang];
  return (
    <div className="ctm-screen" data-screen="bifurcation">
      <header className="ctm-head">
        <h2 className="ctm-title mono" id="ctm-title">{tx.bifTitle}</h2>
        <p className="ctm-sub">{tx.bifSub}</p>
      </header>
      <div className="ctm-options">
        <button type="button" className="ctm-option" onClick={() => onPick("empresa")}>
          <span className="ctm-option-name mono">{tx.bifOpt1Name}</span>
          <span className="ctm-option-desc">{tx.bifOpt1Desc}</span>
          <span className="ctm-option-arrow" aria-hidden="true">→</span>
        </button>
        <button type="button" className="ctm-option" onClick={() => onPick("designer")}>
          <span className="ctm-option-name mono">{tx.bifOpt2Name}</span>
          <span className="ctm-option-desc">{tx.bifOpt2Desc}</span>
          <span className="ctm-option-arrow" aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
}

function ContratameEmpresa({ lang }) {
  const d = CONTRATAME_EMPRESA;
  return (
    <div className="ctm-screen" data-screen="empresa">
      <header className="ctm-head">
        <span className="ctm-eyebrow tag">{pick(d.eyebrow, lang)}</span>
        <h2 className="ctm-title mono" id="ctm-title">{pick(d.title, lang)}</h2>
        <p className="ctm-sub">{pick(d.sub, lang)}</p>
      </header>

      <ul className="ctm-services">
        {d.services.map((s) => (
          <li key={pick(s.name, "es")} className={`ctm-service${s.custom ? " ctm-service--custom" : ""}`}>
            <h3 className="ctm-service-name mono">{pick(s.name, lang)}</h3>
            {!s.custom && (
              <div className="ctm-service-price">
                <span className="ctm-service-price-v mono">{pick(s.price, lang)}</span>
                <span className="ctm-service-price-u mono">{pick(s.unit, lang)}</span>
              </div>
            )}
            {s.custom && (
              <span className="ctm-service-badge mono">{pick(s.customBadge, lang)}</span>
            )}
            <p className="ctm-service-desc">{pick(s.desc, lang)}</p>
            {s.note && <p className="ctm-service-note mono">{pick(s.note, lang)}</p>}
          </li>
        ))}
      </ul>

      {d.pricesNote && (
        <p className="ctm-prices-note mono">{pick(d.pricesNote, lang)}</p>
      )}

      <div className="ctm-cta-row">
        <a className="btn btn--solid" href={`mailto:${d.cta.email}`}>
          {pick(d.cta.label, lang)} → {d.cta.email}
        </a>
      </div>
    </div>
  );
}

function ContratameDesigner({ lang }) {
  const d = CONTRATAME_DESIGNER;
  return (
    <div className="ctm-screen" data-screen="designer">
      <header className="ctm-head">
        <span className="ctm-eyebrow tag">{pick(d.eyebrow, lang)}</span>
        <h2 className="ctm-title mono" id="ctm-title">{pick(d.title, lang)}</h2>
      </header>

      <p className="ctm-pitch">
        {pick(d.pitchLead, lang)}
        <strong>{pick(d.pitchStrong, lang)}</strong>
      </p>

      <ul className="ctm-services">
        {d.modalities.map((m) => (
          <li key={pick(m.name, "es")} className={`ctm-service${m.badge ? " ctm-service--soldout" : ""}`}>
            <h3 className="ctm-service-name mono">
              {pick(m.name, lang)}
              {m.badge && <span className="ctm-service-badge mono ctm-service-badge--soldout">{pick(m.badge, lang)}</span>}
            </h3>
            {m.price && (
              <div className="ctm-service-price">
                <span className="ctm-service-price-v mono">
                  {m.pricePrefix && (
                    <span className="ctm-service-price-currency">{m.pricePrefix} </span>
                  )}
                  {pick(m.price, lang)}
                </span>
                <span className="ctm-service-price-u mono">{pick(m.unit, lang)}</span>
              </div>
            )}
            <p className="ctm-service-desc">{pick(m.desc, lang)}</p>
            {m.cta && (
              <a className="btn btn--solid ctm-service-cta" href={m.cta.href} target="_blank" rel="noreferrer noopener">
                {pick(m.cta.label, lang)} <span className="arrow" aria-hidden="true">→</span>
              </a>
            )}
            {m.ctaNote && (
              <p className="ctm-service-cta-note">{pick(m.ctaNote, lang)}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Modal raíz ───────────────────────────────────────────────────────────
function ContratameModal() {
  const lang = useDocumentLang();
  const tx = TXT[lang];
  const [open, setOpen] = React.useState(false);
  const [screen, setScreen] = React.useState("bifurcation");
  const dialogRef = React.useRef(null);
  const returnFocusRef = React.useRef(null);

  useBodyScrollLock(open);

  const openModal = React.useCallback((trigger) => {
    if (trigger && trigger.focus) returnFocusRef.current = trigger;
    setScreen("bifurcation");
    setOpen(true);
  }, []);

  const closeModal = React.useCallback(() => {
    setOpen(false);
    // restore focus al elemento que disparó la apertura
    setTimeout(() => {
      const el = returnFocusRef.current;
      if (el && typeof el.focus === "function") el.focus();
    }, 0);
  }, []);

  // Expongo globalmente para que cualquier código pueda dispararlo.
  React.useEffect(() => {
    window.openContratameModal = (trigger) => openModal(trigger);
    return () => { try { delete window.openContratameModal; } catch (_) {} };
  }, [openModal]);

  // Click delegation: cualquier mailto a anabelensevillaull@gmail.com o cualquier
  // botón/link con texto "Contratame" / "Hire me" abre el modal.
  React.useEffect(() => {
    const onClick = (e) => {
      const target = e.target && e.target.closest && e.target.closest("a, button");
      if (!target) return;
      // No interceptar el botón "Escribime" / "Write me" dentro del propio modal.
      if (target.closest(".contratame-modal")) return;
      const href = target.getAttribute("href") || "";
      const text = (target.textContent || "").trim();
      const isMailToAnita = href.toLowerCase().includes("mailto:anabelensevillaull@gmail.com");
      const isContratameText = /^(contratame|hire me)\b/i.test(text);
      if (isMailToAnita || isContratameText) {
        e.preventDefault();
        openModal(target);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [openModal]);

  // ESC cierra
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeModal]);

  // Focus al abrir
  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const el = dialogRef.current;
      if (!el) return;
      const firstBtn = el.querySelector(".ctm-close") || el.querySelector("button, a");
      firstBtn && firstBtn.focus && firstBtn.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [open, screen]);

  if (!open) return null;

  const showBack = screen !== "bifurcation";
  const goBack = () => setScreen("bifurcation");

  return (
    <div
      className="contratame-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
    >
      <div
        ref={dialogRef}
        className="contratame-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ctm-title"
      >
        {/* Topbar sticky — siempre visible mientras scrolleás el contenido.
            Back (cuando aplica) y close alineados al mismo eje vertical. */}
        <div className="ctm-topbar">
          {showBack ? (
            <button type="button" className="ctm-back mono" onClick={goBack}>
              <span aria-hidden="true">← </span>{tx.back}
            </button>
          ) : (
            <span className="ctm-topbar-spacer" aria-hidden="true" />
          )}
          <button
            type="button"
            className="ctm-close"
            aria-label={tx.close}
            onClick={closeModal}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {screen === "bifurcation" && <ContratameBifurcation onPick={setScreen} lang={lang} />}
        {screen === "empresa" && <ContratameEmpresa lang={lang} />}
        {screen === "designer" && <ContratameDesigner lang={lang} />}
      </div>
    </div>
  );
}

window.ContratameModal = ContratameModal;
